import { sharePointHelpers } from '@qdabra/forms-shared/sharePoint';
import { ensureSlash } from '@qdabra/forms-shared/urlHelpers';
import {
    __,
    always,
    assoc,
    bind,
    chain,
    complement,
    compose,
    concat,
    curry,
    filter,
    flatten,
    flip,
    fromPairs,
    has,
    head,
    identity,
    ifElse,
    includes,
    invoker,
    isEmpty,
    isNil,
    map,
    not,
    nth,
    pick,
    prop,
    splitAt,
    toPairs,
    uniq,
    when,
    zip,
} from 'ramda';
import { encode as utf8Encode } from 'utf8';
import { ensureScript } from '../../scriptLoader';
import {
    getXmlDate,
    getXmlDateTime,
    getXmlTime,
    isDate,
} from '../../utility/date.helpers';
import { composeP } from '../../utility/promise.helpers';
import { xmlToString } from '../../xmlUtility';
import { TERMSET_QUERY_IDS, TERMSET_QUERY_LABEL } from './sharePoint.constants';
import {
    FIELD_TYPE_ATTACHMENTS,
    FIELD_TYPE_DATETIME,
    FIELD_TYPE_LOOKUP,
    FIELD_TYPE_USER,
} from './sharePoint.fieldTypes';
import { termSetsToObjects } from './sharePoint.termSet.object.convert';
import { getUserProfileValuesFromUser } from './sharePoint.userProfile';
import { getSpErrorMsg } from './sharePointClientSide.queryHelpers';
import { appWebUrl, hostWebUrl, isInHostWebDomain } from './sharePointHelper';

const { getDomainFromUrl, restString } = sharePointHelpers;

const { parseXML } = window.jQuery;
// SharePoint scripts are loaded on demand in initScriptLoad()
let SP;

const mimeJson = 'application/json';
const mimeJsonVerbose = `${mimeJson};odata=verbose`;
const mimeJsonNometa = `${mimeJson};odata=nometadata`;

const acceptTypeJson = `${mimeJsonNometa},${mimeJsonVerbose}`;

const initScriptLoad = async () => {
    try {
        await ensureScript('sharePoint');

        SP = window.SP;
    } catch (e) {
        console.error('Error loading SharePoint scripts', e);
    }
};

const spApiFunc =
    (func) =>
    async (...args) => {
        await initScriptLoad();

        return func(...args);
    };

const unspecifiedErrorMessage = 'Unspecified SharePoint error';

function parseJsonError(errBody) {
    var parsed = JSON.parse(errBody);
    var error = parsed && (parsed.error || parsed['odata.error']);
    var message = error && error.message;

    return message && message.value;
}

function parseXmlError(errBody) {
    var x = parseXML(errBody);

    var message = x.getElementsByTagNameNS(
        'http://schemas.microsoft.com/ado/2007/08/dataservices/metadata',
        'message',
    )[0];

    return message ? message.textContent : errBody;
}

function parseRestResponseErrorMessage(body, contentType) {
    var lowerContent = contentType.toLowerCase();

    if (lowerContent.indexOf('/xml') !== -1) {
        return parseXmlError(body);
    }
    if (lowerContent.indexOf('/json') !== -1) {
        return parseJsonError(body);
    }

    return null;
}

function getErrorFromRestResponse(err) {
    var body = err.body;
    var contentType = err.contentType || '';

    if (!body) {
        return unspecifiedErrorMessage;
    }

    try {
        return parseRestResponseErrorMessage(body, contentType) || body;
    } catch (e) {
        return unspecifiedErrorMessage;
    }
}

// =========================================================================
// Query execution - Converting context/requestExecutor requests to promises
// =========================================================================

// Executes a request executor request and returns a promise for the result
function executeRequestWithPromise(executor, params, showWait) {
    var executePromise = new Promise(function (resolve, reject) {
        params.success = resolve;
        params.error = function (xhr) {
            reject(xhr);
        };

        executor.executeAsync(params);
    });

    return showWait ? showWait(executePromise) : executePromise;
}

// (Executor, RestRequestOptiont) -> ExecutorResult
function makeRestRequestAsync(executor, params, showWait) {
    return executeRequestWithPromise(executor, params, showWait).catch(
        function (err) {
            var newErr = new Error(getErrorFromRestResponse(err));
            newErr.xhr = err.xhr;
            throw newErr;
        },
    );
}

// (Executor, RestRequestOptions, ShowWait) => String
const makeRestRequestToBodyAsync = composeP([
    prop('body'),
    makeRestRequestAsync,
]);

// (Executor, RestRequestOptions, ShowLoader) => Any
const makeRestRequestToJsonAsync = composeP([
    JSON.parse,
    makeRestRequestToBodyAsync,
]);

const makeRestRequestToJsonDataAsync = composeP([
    ifElse(has('d'), prop('d'), identity),
    makeRestRequestToJsonAsync,
]);

const getMessageFromErrorResult = (err) =>
    err?.get_message?.() || err?.message || '(unknown error)';

// Executes a query and returns a promise for the result
function executeQueryWithPromiseImpl(context, showWait) {
    const executePromise = new Promise(function (resolve, reject) {
        function handleError(req, error) {
            const e = new Error(getMessageFromErrorResult(error));

            reject(e);
        }

        context.executeQueryAsync(resolve, handleError);
    });

    return showWait ? showWait(executePromise) : executePromise;
}

function getIdNode(fieldArray, node) {
    var filteredColumn = fieldArray.filter(function (item) {
        return item.isId;
    });

    if (filteredColumn && filteredColumn.length > 0) {
        return node.selectSingle(filteredColumn[0].xPath);
    }
    return null;
}

function fieldRef(doc, name, { type, sortAsc } = {}) {
    var el = doc.createElement('FieldRef');
    el.setAttribute('Name', name);
    if (type) {
        el.setAttribute('Type', type);
        if (type.toLowerCase() === 'lookup') {
            el.setAttribute('LookupId', 'TRUE');
        }
    }
    if (typeof sortAsc !== 'undefined') {
        el.setAttribute('Ascending', sortAsc.toString().toUpperCase());
    }

    return el;
}

function valueElement(doc, type, value) {
    const el = doc.createElement('Value');
    el.setAttribute('Type', type);
    el.textContent = value;

    return el;
}

function inFilter(doc, fieldRefEl, internalName, type, values) {
    if (values.length === 0) {
        throw new Error(
            `Cannot query on an empty list of values for column ${internalName}`,
        );
    }

    const inEl = doc.createElement('In');
    const valuesEl = doc.createElement('Values');

    inEl.appendChild(fieldRefEl);
    inEl.appendChild(valuesEl);

    values.forEach((value) =>
        valuesEl.appendChild(valueElement(doc, type, value)),
    );

    return inEl;
}

function eqFilter(doc, fieldRefEl, type, value) {
    const eq = doc.createElement('Eq');

    eq.appendChild(fieldRefEl);
    eq.appendChild(valueElement(doc, type, value));

    return eq;
}

function createFilter(
    viewXml,
    { field: { internalName, type }, value, values },
) {
    const fieldRefEl = fieldRef(viewXml, internalName, { type });

    if (values) {
        return inFilter(viewXml, fieldRefEl, internalName, type, values);
    }

    return eqFilter(viewXml, fieldRefEl, type, value);
}

// Given an array of single-field filter elements, returns a binary hierarchies of <And> elements joining them.
// If filters is of length 1, just returns that element.
// (DOMDocument, [DOMElement]) -> DOMElement
function joinFilters(viewXml, filters) {
    if (filters.length === 1) {
        return filters[0];
    }

    const and = viewXml.createElement('And');

    splitAt(filters.length >> 1, filters).forEach((side) =>
        and.appendChild(joinFilters(viewXml, side)),
    );

    return and;
}

function addFilter(viewXml, filterFields) {
    const viewElement = viewXml.documentElement;

    const filters = filterFields
        .map((ff) => createFilter(viewXml, ff))
        .filter(complement(isNil));

    if (filters.length) {
        const query = viewElement.appendChild(viewXml.createElement('Query'));
        const where = query.appendChild(viewXml.createElement('Where'));

        where.appendChild(joinFilters(viewXml, filters));
    }
}

function addOrderBy(viewXml, sortBy, sortAsc) {
    var viewElement = viewXml.documentElement,
        query =
            viewXml.documentElement.querySelector('Query') ||
            viewElement.appendChild(viewXml.createElement('Query')),
        orderBy = query.appendChild(viewXml.createElement('OrderBy'));

    orderBy.appendChild(fieldRef(viewXml, sortBy, { sortAsc }));
}

// adds a queryOptions element
function addQueryOptions(viewXml) {
    var viewElement = viewXml.documentElement;

    var queryOptions = parseXML(
        '<QueryOptions><IncludeAttachmentUrls>true</IncludeAttachmentUrls><ExpandUserField>true</ExpandUserField><ViewAttributes Scope="RecursiveAll" /></QueryOptions>',
    );

    viewElement.appendChild(queryOptions.documentElement);
}

// adds a ViewFields element
function addViewFields(viewXml, fields) {
    const viewElement = viewXml.documentElement;
    const viewFields = viewElement.appendChild(
        viewXml.createElement('ViewFields'),
    );
    fields.forEach((name) => {
        const viewField = fieldRef(viewXml, name);
        viewFields.appendChild(viewField);
    });

    viewElement.appendChild(viewFields);
}

function stringToBase64ByteArray(strValue) {
    const u8 = utf8Encode(strValue);
    let b64 = new SP.Base64EncodedByteArray();

    for (var i = 0; i < u8.length; i += 1) {
        b64.append(u8.charCodeAt(i));
    }

    return b64;
}

const getFieldValues = invoker(0, 'get_fieldValues');

// ========================
// String processing
// ========================

var serverRootRegex = /^[^:]+:\/\/[^/]+/;

function getServerRelativePath(fullPath) {
    return fullPath.replace(serverRootRegex, '');
}

function getServerRoot(fullPath) {
    var result = serverRootRegex.exec(fullPath);

    return result ? result[0] : '';
}

// requires ShP scripts
function prepareFile(name, strContent, isBase64, overwrite) {
    var fileContent = isBase64
        ? strContent
        : stringToBase64ByteArray(strContent);

    var fileCreateInfo = new SP.FileCreationInformation();
    fileCreateInfo.set_url(name);
    fileCreateInfo.set_content(fileContent);
    fileCreateInfo.set_overwrite(overwrite);

    return fileCreateInfo;
}

// returns the portion of url up to the last slash
function getBasePath(url) {
    // SP.AppContextSite() requires the scheme and domain
    return /^https?:\/\//.test(url)
        ? url.substring(0, url.lastIndexOf('/'))
        : null;
}

const nonApiMembers = ['type'];

const wrapApiMethods = compose(
    fromPairs,
    map(
        when(
            ([name]) => !includes(name, nonApiMembers),
            ([name, member]) => [name, spApiFunc(member)],
        ),
    ),
    toPairs,
);

function sharePointClientSideAccess({ showWait } = {}) {
    // start script loading right away.
    // intentionally not awaited so it runs in background
    initScriptLoad();

    var userInfoCache = {};

    // UserIdCache is { Number: { accountId: String, displayName: String } }
    // { String : UserIdCache }
    let userIdCaches = {};

    const resolve = bind(Promise.resolve, Promise);

    var hostRootSiteUrl = null;

    function getHostWebContext(context, url) {
        return new SP.AppContextSite(context, url || hostWebUrl);
    }

    // SpContext[, siteUrl] -> SpWeb
    function getWeb(context, url) {
        var hostContext = getHostWebContext(context, url);

        return hostContext.get_web();
    }

    function getExecutorPath(request, basePath) {
        const basePathEscaped = restString(basePath || hostWebUrl);

        return `${appWebUrl}/_api/SP.AppContextSite(@target)/${request}?@target=${basePathEscaped}`;
    }

    function getExecutor(domain) {
        return new SP.RequestExecutor(domain || appWebUrl);
    }

    function setExecutor(clientContext) {
        var factory = new SP.ProxyWebRequestExecutorFactory(appWebUrl);
        clientContext.set_webRequestExecutorFactory(factory);
    }

    function getContext() {
        var context = new SP.ClientContext(appWebUrl);
        setExecutor(context);
        return context;
    }

    const executeQueryWithPromise = (context) =>
        executeQueryWithPromiseImpl(context, showWait);

    function getDigestAsync() {
        var context = getContext();

        return resolve()
            .then(function () {
                // this will throw an error if the digest info is not yet cached.
                // so we catch and retry after executing a query if it throws
                return context.get_formDigestInfo();
            })
            .catch(function () {
                return executeQueryWithPromise(context).then(function () {
                    return context.get_formDigestInfo();
                });
            })
            .then((d) => d.get_digestValue());
    }

    // String -> UserIdCache
    function getUserIdCache(siteUrl) {
        const lower = siteUrl.toLowerCase();

        if (!userIdCaches[lower]) {
            userIdCaches[lower] = {};
        }

        return userIdCaches[lower];
    }

    // Returns the list of user ids that are not in the cache for the indicated site URL
    // (String, [Number]) -> [Number]
    function getNeededUserIds(siteUrl, allIds) {
        const neededIdMap = allIds.reduce((idMapAcc, id) => {
            idMapAcc[id] = true;
            return idMapAcc;
        }, {});
        const cache = getUserIdCache(siteUrl);
        const notInCache = complement(flip(has)(cache));

        return Object.keys(neededIdMap).filter(notInCache).map(Number);
    }

    // (String, UserIdCache) -> UserIdCache
    function updateUserIdCache(siteUrl, newValues) {
        let cache = getUserIdCache(siteUrl);

        Object.assign(cache, newValues);

        return cache;
    }

    // requires ShP scripts
    function submitFileWithClientContextAsync(
        folderUrl,
        fileName,
        strXml,
        isBase64,
        overwrite,
    ) {
        var clientContext = getContext(),
            siteUrl = folderUrl.replace(/[^/]+\/?$/, ''),
            hostWebContext = getWeb(clientContext, siteUrl),
            fileCreateInfo = prepareFile(fileName, strXml, isBase64, overwrite),
            newFile = hostWebContext
                .getFolderByServerRelativeUrl(getServerRelativePath(folderUrl))
                .get_files()
                .add(fileCreateInfo);

        clientContext.load(newFile);

        return executeQueryWithPromise(clientContext).catch(function (error) {
            throw new Error(error.shpError.get_message());
        });
    }

    function submitFileWithRequestExecutorAsync(
        fullRequestPath,
        fileContent,
        isBase64,
    ) {
        const executor = getExecutor();

        return getDigestAsync().then(function (digest) {
            return makeRestRequestAsync(
                executor,
                {
                    url: fullRequestPath,
                    method: 'POST',
                    // body expects a "binary string" when saving binary values
                    body: isBase64 ? atob(fileContent) : fileContent,
                    binaryStringRequestBody: isBase64,
                    headers: {
                        accept: acceptTypeJson,
                        'X-RequestDigest': digest,
                    },
                },
                showWait,
            );
        });
    }

    function submitFileToApiPathAsync(siteUrl, apiPath, fileContent) {
        const fullRequestPath = getExecutorPath(apiPath, siteUrl);
        return submitFileWithRequestExecutorAsync(
            fullRequestPath,
            fileContent,
            true,
        ).then((data) => JSON.parse(data.body));
    }

    function fileExistsAsync(fileUrl) {
        var context = getContext(),
            siteUrl = getDomainFromUrl(fileUrl),
            web = getWeb(context, fileUrl);

        if (!isInHostWebDomain(fileUrl)) {
            return resolve(false);
        }

        fileUrl = fileUrl.replace(siteUrl, '');
        if (fileUrl[0] !== '/') {
            fileUrl = '/' + fileUrl;
        }

        var spFile = web.getFileByServerRelativeUrl(fileUrl);

        context.load(spFile);

        return executeQueryWithPromise(context)
            .then(function () {
                return true;
            })
            .catch(function () {
                return false;
            });
    }

    function performOverwriteCheck(fileUrl, overwrite) {
        if (!overwrite) {
            //return false if file exists
            return fileExistsAsync(fileUrl).then(not);
        }
        return Promise.resolve(true);
    }

    function performLibrarySubmit(
        folderUrl,
        fileName,
        fileContent,
        overwrite,
        isBase64,
        baseUrl,
    ) {
        const relFolderUrl = getServerRelativePath(folderUrl);
        const requestPath = `web/GetFolderByServerRelativeUrl(${restString(
            relFolderUrl,
        )})/Files/add(url=${restString(fileName)},overwrite=${overwrite})`;
        const fullRequestPath = getExecutorPath(requestPath, baseUrl);

        return submitFileWithRequestExecutorAsync(
            fullRequestPath,
            fileContent,
            isBase64,
        ).catch(function () {
            // include old approach as a fallback
            return submitFileWithClientContextAsync(
                folderUrl,
                fileName,
                fileContent,
                isBase64,
                overwrite,
            );
        });
    }

    function submitToLibraryAsync(
        folderUrl,
        fileName,
        fileContent,
        overwrite,
        isBase64,
    ) {
        const sanitizedFolderUrl = ensureSlash(folderUrl);
        const baseUrl = getBasePath(sanitizedFolderUrl);
        const fileUrl = sanitizedFolderUrl + fileName;

        return performOverwriteCheck(fileUrl, overwrite)
            .then((checkSucceeded) => {
                if (!checkSucceeded) {
                    const errorMessage = `Cannot submit ${fileName}\n\nA file named ${fileName} already exists in ${sanitizedFolderUrl}.`;
                    let error = new Error(errorMessage);
                    error.userDisplayMessage = errorMessage;

                    throw error;
                }
                return performLibrarySubmit(
                    sanitizedFolderUrl,
                    fileName,
                    fileContent,
                    overwrite,
                    isBase64,
                    baseUrl,
                );
            })
            .then(always({ success: true }));
    }

    // String -> String
    function getFileRequestPath(url) {
        var serverRelativeUrl = getServerRelativePath(url);
        // SP.AppContextSite() requires the scheme and domain
        var baseUrl = getBasePath(url);

        return getExecutorPath(
            'web/GetFileByServerRelativeUrl(' +
                restString(serverRelativeUrl) +
                ')/$value',
            baseUrl,
        );
    }

    // String -> { target: String, path: String, params: String }
    function splitApiString(url) {
        var r = /^(.+?\/)_api\/(.+?)(\?(.+))?$/;

        var m = r.exec(url);

        if (!m) {
            return null;
        }

        return {
            target: m[1],
            path: m[2],
            params: m[4] || null,
        };
    }

    // String -> String
    function getApiRequestPath(url) {
        var parts = splitApiString(url);

        if (!parts) {
            throw new Error(
                'Unexpected error. Could not parse the REST URL: ' + url,
            );
        }

        return (
            getExecutorPath(parts.path, parts.target) +
            (parts.params ? '&' + parts.params : '')
        );
    }

    function queryXmlAsync(url) {
        var executor = getExecutor();

        var requestPath =
            url.toLowerCase().indexOf('/_api/') !== -1
                ? getApiRequestPath(url)
                : getFileRequestPath(url);

        return makeRestRequestToBodyAsync(
            executor,
            {
                url: requestPath,
                type: 'GET',
                headers: {
                    accept: 'text/xml',
                },
            },
            showWait,
        );
    }

    const ensureUserRestAsync = curry(
        function (logonName, webUrl, executor, digest) {
            const url = getExecutorPath('web/ensureuser', webUrl);
            const body = JSON.stringify({ logonName });

            return makeRestRequestToJsonDataAsync(
                executor,
                {
                    url,
                    body,
                    method: 'POST',
                    headers: {
                        accept: acceptTypeJson,
                        // some SP On-Prem environments only support odata verbose
                        'Content-Type': mimeJsonVerbose,
                        'X-RequestDigest': digest,
                    },
                },
                showWait,
            );
        },
    );

    const getSiteUserRestAsync = curry(function (id, webUrl, executor) {
        const url = getExecutorPath(
            `web/siteuserinfolist/items(${id})`,
            webUrl,
        );

        return makeRestRequestToJsonDataAsync(
            executor,
            {
                url,
                method: 'GET',
                headers: {
                    accept: acceptTypeJson,
                },
            },
            showWait,
        );
    });

    function getSiteUserAsync(userName) {
        const executor = getExecutor();

        return getDigestAsync()
            .then(ensureUserRestAsync(userName, hostWebUrl, executor))
            .then(prop('Id'))
            .then(getSiteUserRestAsync(__, hostWebUrl, executor));
    }

    // 2018-04-17 - Removed unused getFile() function

    function processListFields(fields, columns) {
        return fields.get_data().reduce(function (fieldTypes, field) {
            const internalName = field.get_internalName();
            const type = field.get_fieldTypeKind();
            const fieldIsDate = type === FIELD_TYPE_DATETIME;

            // BUGBUG - We should probably be doing a strict name match here
            const isMatch = columns.some(
                (column) =>
                    column &&
                    column.toLowerCase() === internalName.toLowerCase(),
            );
            if (!isMatch) {
                return fieldTypes;
            }

            const fieldType = {
                type,
                isRichText: field.get_richText && field.get_richText(),
                isDate: fieldIsDate,
            };

            if (type === FIELD_TYPE_LOOKUP) {
                fieldType.allowMultiple = field.get_allowMultipleValues();
            }

            fieldTypes[internalName] = fieldType;
            return fieldTypes;
        }, {});
    }

    function getDateTimeConvertedValues(valueArr, timeZone) {
        return valueArr
            .filter((v) => v && v.toISOString)
            .map((v) => timeZone.utcToLocalTime(v.toISOString()));
    }

    function getAttachmentFiles(lItem, serverRoot) {
        var files = lItem.get_attachmentFiles().get_data();

        return files.map(function (file) {
            return serverRoot + file.get_serverRelativeUrl();
        });
    }

    function getTimeConversionInfo(web) {
        const timeZone = web.get_regionalSettings().get_timeZone();

        const refDate = new Date();
        const localTime = timeZone.utcToLocalTime(refDate.toISOString());

        const getOffset = () => localTime.get_value() - refDate;
        const addOffset = (date, offset) => new Date(+date + offset);

        // Methods on the returned object are not usable until a query has
        // been executed on the associated SharePoint context
        return {
            // The offset, in milliseconds, between the SharePoint local time and UTC
            // If the local time is behind UTC, the result will be negative
            // () -> Number
            getOffset,

            // Returns a Date object whose LOCAL date value is shifted by the current offset
            // relative to the UTC value of the provided date object
            // Date -> Date
            utcToLocal: (utcDate) => addOffset(utcDate, getOffset()),

            // Returns a Date object whose UTC date value is shifted by the current offset
            // relative to the LOCAL value of the provided date object
            // Date -> Date
            localToUtc: (localDate) => addOffset(localDate, -getOffset()),
        };
    }

    function isPersonField(fieldType) {
        return fieldType === FIELD_TYPE_USER;
    }

    function getItemValues(
        context,
        lItem,
        fieldType,
        values,
        key,
        web,
        serverRoot,
    ) {
        if (fieldType === FIELD_TYPE_ATTACHMENTS) {
            return {
                fieldType: fieldType,
                values: getAttachmentFiles(lItem, serverRoot),
            };
        }

        // could be an array or single value, so normalize to an array
        var valueArr = isNil(values[key]) ? [] : [].concat(values[key]);
        var valueItem = {
            fieldType: fieldType,
            values: valueArr,
        };

        if (fieldType === FIELD_TYPE_DATETIME) {
            valueItem.convertedValue = getDateTimeConvertedValues(
                valueArr,
                web.get_regionalSettings().get_timeZone(),
            );
        }

        if (isPersonField(fieldType)) {
            valueItem.values = valueItem.values.map((v) => ({
                lookupId: v.get_lookupId(),
            }));
        }

        return valueItem;
    }

    function getUserIds(web, id) {
        const context = web.get_context();
        const siteUserList = web.get_siteUserInfoList();

        const siteUser = siteUserList.getItemById(id);
        context.load(siteUser, 'ID', 'Title', 'Name');

        return siteUser;
    }

    function processListItem(lItem, fieldType, context, web, serverRoot) {
        var values = lItem.get_fieldValues(),
            listItem = {};

        Object.keys(values).forEach(function (key) {
            var fieldDef = fieldType[key];
            if (!fieldDef) {
                console.warn(
                    'Column: ' + key + ' not found in field definition.',
                );
                return;
            }

            listItem[key] = getItemValues(
                context,
                lItem,
                fieldDef.type,
                values,
                key,
                web,
                serverRoot,
            );
        });

        return listItem;
    }

    function processListItems(items, siteUrl, fieldType, context, web) {
        var serverRoot = getServerRoot(siteUrl);

        return items.map((lItem) =>
            processListItem(lItem, fieldType, context, web, serverRoot),
        );
    }

    const processFields = curry(function (action, fieldNames, items) {
        items.forEach((item) =>
            fieldNames.forEach((field) => {
                item[field].values = action(item[field]);
            }),
        );

        return items;
    });

    // ([String], [ProcessedListItem], UserIdCache) -> [ProcessedListItem]
    const populatePersonFields = (personFields, items, userIdCache) =>
        processFields((value) =>
            value.values.map((l) => userIdCache[l.lookupId]).filter(identity),
        )(personFields, items);

    const getAllPersonIdsForListItem = curry((personFieldNames, item) => {
        return compose(
            map(prop('lookupId')),
            flatten,
            map((fieldName) => item[fieldName].values),
        )(personFieldNames);
    });

    // ([String], [ListItem]) -> [Number]
    const getAllPersonIdsForListItems = (personFieldNames, items) =>
        compose(
            flatten,
            map(getAllPersonIdsForListItem(personFieldNames)),
        )(items);

    const getFieldsOfType = curry((fieldType, fieldTypes) =>
        Object.entries(fieldTypes)
            .filter(([, { type }]) => type === fieldType)
            .map(nth(0)),
    );

    // (Web, [Number]) => ListItem
    const initiatePersonIdQueries = (web, userIds) =>
        userIds.map((id) => getUserIds(web, id));

    // (SPContext, [String], [ProcessedListItem]) -> [ListItem]
    function checkNeededPeople(web, personFields, items) {
        const allUserIds = getAllPersonIdsForListItems(personFields, items);
        const neededUserIds = getNeededUserIds(web.get_url(), allUserIds);

        return initiatePersonIdQueries(web, neededUserIds);
    }

    function convertDateToString(date) {
        return getXmlDate(date) + 'T' + getXmlTime(date);
    }

    // ([String], [ProcessedListItem]) => [ProcessedListItem]
    const processQueriedDateFields = processFields((value) =>
        value.convertedValue.map(
            (v) => v && convertDateToString(v.get_value()),
        ),
    );

    // Creates a map of people that have been queried and adds it to the user cache
    // (SP.Web, [SP.Item]) -> UserIdCache
    function processQueriedPeople(web, queryPeople) {
        const newUserMap = queryPeople
            .map(getFieldValues)
            .reduce((personMap, { ID, Title, Name }) => {
                personMap[ID] = { displayName: Title, accountId: Name };
                return personMap;
            }, {});

        return updateUserIdCache(web.get_url(), newUserMap);
    }

    const processQueryListResultAsync = async (
        context,
        siteUrl,
        columns,
        listFields,
        listItems,
        web,
    ) => {
        const itemsData = listItems.get_data();
        const fieldTypes = processListFields(listFields, columns);

        const personFields = getFieldsOfType(FIELD_TYPE_USER, fieldTypes);
        const dateFields = getFieldsOfType(FIELD_TYPE_DATETIME, fieldTypes);

        const items = processListItems(
            itemsData,
            siteUrl,
            fieldTypes,
            context,
            web,
        );
        const queryPeople = checkNeededPeople(web, personFields, items);
        const shouldQuery = dateFields.length || queryPeople.length;

        if (shouldQuery) {
            await executeQueryWithPromise(context);
        }

        populatePersonFields(
            personFields,
            items,
            processQueriedPeople(web, queryPeople),
        );

        processQueriedDateFields(dateFields, items);

        return { items, fieldTypes };
    };

    function hasAttachmentColumn(columns) {
        return columns.some(function (colName) {
            return colName.match(/^attachments$/i);
        });
    }

    const prepareListQueryViewXml = (filterFields, sortBy, sortAsc, fields) => {
        const viewXml = parseXML("<View Scope='RecursiveAll'/>");

        if (filterFields.length) {
            addFilter(viewXml, filterFields);
        }

        if (sortBy) {
            addOrderBy(viewXml, sortBy, sortAsc);
        }

        addQueryOptions(viewXml);

        addViewFields(viewXml, fields);

        return viewXml;
    };

    const prepareSpListQuery = (filterFields, sortBy, sortAsc, fields) => {
        const camlQuery = new SP.CamlQuery();

        const viewXml = prepareListQueryViewXml(
            filterFields,
            sortBy,
            sortAsc,
            fields,
        );

        camlQuery.set_viewXml(xmlToString(viewXml));

        return camlQuery;
    };

    async function querySpListAsync({
        listId,
        listName,
        fields,
        filterFields,
        siteUrl,
        sortBy,
        sortAsc,
    }) {
        const context = getContext();
        const web = getWeb(context, siteUrl);
        const camlQuery = prepareSpListQuery(
            filterFields,
            sortBy,
            sortAsc,
            fields,
        );
        const lists = web.get_lists();
        const list = listId
            ? lists.getById(listId)
            : lists.getByTitle(listName);

        const includeColumns = hasAttachmentColumn(fields)
            ? fields.concat('AttachmentFiles')
            : fields;

        const listItems = list.getItems(camlQuery);
        const columnStr = `Include(${includeColumns.join(',')})`;
        const listFields = list.get_fields();

        context.load(listItems, columnStr);
        context.load(listFields);
        // load site URL so we can access it from the web object
        context.load(web, 'Url');

        try {
            await executeQueryWithPromise(context);

            return await processQueryListResultAsync(
                context,
                siteUrl,
                fields,
                listFields,
                listItems,
                web,
            );
        } catch (error) {
            console.error('Error occurred trying to execute query.', error);
            const errorMessage = error.shpError
                ? error.shpError.get_message()
                : error.message;
            throw new Error(errorMessage);
        }
    }

    function getUserProfileProperties(context, userName, profilePropertyNames) {
        var peopleManager = new SP.UserProfiles.PeopleManager(context);

        // Specify the properties to retrieve
        var userProfilePropertiesForUser =
            new SP.UserProfiles.UserProfilePropertiesForUser(
                context,
                userName,
                profilePropertyNames,
            );

        // Get user profile properties for the target user using the UserProfilePropertyFor method.
        var userProfileProperties = peopleManager.getUserProfilePropertiesFor(
            userProfilePropertiesForUser,
        );

        // Load the UserProfilePropertiesForUser object and send the request.
        context.load(userProfilePropertiesForUser);

        return userProfileProperties;
    }

    const combinePropNamesAndValues = (propNames, propValues) =>
        compose(fromPairs, zip)(propNames, propValues);

    const getExtraPropertiesInner = async (userName, profilePropertyNames) => {
        if (!(profilePropertyNames && profilePropertyNames.length)) {
            return {};
        }

        const context = getContext();

        const userProfileProperties = getUserProfileProperties(
            context,
            userName,
            profilePropertyNames,
        );

        await executeQueryWithPromise(context);

        return combinePropNamesAndValues(
            profilePropertyNames,
            userProfileProperties,
        );
    };

    const getExtraProperties = async (userName, profilePropertyNames) => {
        try {
            return {
                properties: await getExtraPropertiesInner(
                    userName,
                    profilePropertyNames,
                ),
            };
        } catch (error) {
            const message =
                'Error occurred trying to load extra user properties.';
            console.error(message, error);

            return { properties: {}, error: message };
        }
    };

    function getUserLazy(userName) {
        var userNameNormalized = (userName || '').toLowerCase();
        var existingUser = userInfoCache[userNameNormalized];

        if (existingUser) {
            return resolve(existingUser);
        }

        var context = getContext();
        var web = getWeb(context);
        var user = userNameNormalized
            ? web.ensureUser(userNameNormalized)
            : web.get_currentUser();

        context.load(user);

        return executeQueryWithPromise(context).then(function () {
            userInfoCache[userNameNormalized] = user;
            userInfoCache[user.get_loginName()] = user;

            return user;
        });
    }

    const loadUserAsync = async (userName, extraPropertyNames) => {
        const user = await getUserLazy(userName);

        const { properties: extraProperties, error } = await getExtraProperties(
            user.get_loginName(),
            extraPropertyNames,
        );

        const userProperties = getUserProfileValuesFromUser(user);

        return {
            properties: {
                ...userProperties,
                ...extraProperties,
            },
            extraPropertiesError: error,
        };
    };

    function convertListFieldValue(fieldItem) {
        // 53316 - for consistency with return value of list query, the .value property is now named .values and
        //         is always an array, even if the field is not a multi-value field

        if (fieldItem.isDate && fieldItem.convertedDate) {
            return fieldItem.convertedDate.get_value();
        }

        if (fieldItem.isUrl) {
            var value = new SP.FieldUrlValue();

            value.set_url(fieldItem.urlValue.url || '');
            value.set_description(fieldItem.urlValue.description || '');

            return value;
        }

        if (fieldItem.isFieldUserValue) {
            return fieldItem.values.map((v) => SP.FieldUserValue.fromUser(v));
        }

        return head(fieldItem.values);
    }

    // [SubmitItem] -> [[FieldItem, string]]
    const getTimeFields = compose(
        filter(nth(1)),
        map((field) => [field, field.values?.[0]]),
        filter(prop('isDate')),
        chain((item) => item.fieldArray),
    );

    // (SPContext, [SubmitItem]) -> Promise[void]
    const convertDatesAsync = async (context, items) => {
        const timeZone = context
            .get_web()
            .get_regionalSettings()
            .get_timeZone();

        const timeFields = getTimeFields(items);

        if (isEmpty(timeFields)) {
            return;
        }

        for (const [field, value] of timeFields) {
            field.convertedDate = timeZone.localTimeToUTC(value);
        }

        await executeQueryWithPromise(context);
    };

    function getList(web, identifier, useName) {
        return web.get_lists()[useName ? 'getByTitle' : 'getById'](identifier);
    }

    function updateListItemsAsync(context, submitItem) {
        const { siteUrl, itemArray, useName, listName, listId } = submitItem;
        const web = getWeb(context, siteUrl);
        const identifier = useName ? listName : listId;
        const spList = getList(web, identifier, useName);

        itemArray.forEach(function (item) {
            const fieldArray = item.fieldArray;
            const idNode = getIdNode(fieldArray, item.node.node);
            const itemId = idNode ? idNode.value() : '';
            const listItem = itemId
                ? spList.getItemById(itemId)
                : spList.addItem(new SP.ListItemCreationInformation());

            for (const fieldItem of fieldArray) {
                if (fieldItem && fieldItem.internalName) {
                    listItem.set_item(
                        fieldItem.internalName,
                        convertListFieldValue(fieldItem),
                    );
                }
            }

            item.listItem = listItem;

            listItem.update();
            context.load(listItem);
        });

        return executeQueryWithPromise(context);
    }

    // Modified dates are formatted as yyyy:MM:dd HH:mm:ss in the SharePoint site's local time zone
    // SubmitItem -> [{ id: Number, modified: String }]
    function submitToListAsync(submitItem) {
        var context = getContext();

        const conversionInfo = getTimeConversionInfo(context.get_web());

        return convertDatesAsync(context, submitItem.itemArray)
            .then(() => updateListItemsAsync(context, submitItem))
            .then(() => submitItem.itemArray)
            .then(map(prop('listItem')))
            .then(map(getFieldValues))
            .then(
                map((fs) => ({
                    id: fs.ID,
                    modified: getXmlDateTime(
                        conversionInfo.utcToLocal(fs.Modified),
                        { separator: ' ' },
                    ),
                })),
            );
    }

    const determineDeleteFileUrl = (fileUrl, siteUrl) => {
        // BUGBUG - I think there is a more robust way to approach this code
        const withoutSiteUrl = fileUrl.replace(siteUrl, '');

        return withoutSiteUrl[0] === '/'
            ? withoutSiteUrl
            : '/' + withoutSiteUrl;
    };

    const deleteFileAsync = async (fileUrl) => {
        const context = getContext();
        const siteUrl = getDomainFromUrl(fileUrl);
        const web = getWeb(context, fileUrl);

        if (!isInHostWebDomain(fileUrl)) {
            throw new Error('Cannot delete file. Not in host domain.');
        }

        const fileToDelete = web.getFileByServerRelativeUrl(
            determineDeleteFileUrl(fileUrl, siteUrl),
        );

        // It appears the file is being loaded here so that we can get an error in the
        // case that the file doesn't exist. However, I think we can do the load and delete
        // in a single query instead of two separate queries.
        context.load(fileToDelete);

        await executeQueryWithPromise(context);

        fileToDelete.deleteObject();

        await executeQueryWithPromise(context);
    };

    // [MappingField] -> [String]
    const refreshFieldNames = compose(
        uniq,
        concat(['ID', 'Modified']),
        filter(identity),
        map(prop('sharePointColumn')),
    );

    const convertDate = (timeZone) =>
        when(isDate, (date) => ({
            isConvertedDate: true,
            date: timeZone.utcToLocalTime(date.toISOString()),
        }));

    const unwrapConvertedDate = when(prop('isConvertedDate'), ({ date }) =>
        date.get_value(),
    );

    function fixupDates(context, web, listItem, fields) {
        const timeZone = web.get_regionalSettings().get_timeZone();
        const fieldValues = getFieldValues(listItem);
        const fieldNames = refreshFieldNames(fields);
        const neededFieldValues = pick(fieldNames, fieldValues);

        const converted = map(convertDate(timeZone), neededFieldValues);

        return executeQueryWithPromise(context).then(() =>
            map(unwrapConvertedDate, converted),
        );
    }

    function getListItemAsync(siteUrl, list, itemId, fields) {
        var context = getContext();
        var web = getWeb(context, siteUrl);
        var spLists = web.get_lists();
        var spList = list.useName
            ? spLists.getByTitle(list.name)
            : spLists.getById(list.id);
        var spListItem = spList.getItemById(itemId);

        context.load(spListItem);

        return (
            executeQueryWithPromise(context)
                .then(() => spListItem)
                // query will fail with an error if item is not found
                // TODO: differentiate behavior based on error type
                .catch((err) => {
                    console.log(err);
                    return null;
                })
                .then((item) => item && fixupDates(context, web, item, fields))
        );
    }

    function getListIdByNameAsync(siteUrl, listName) {
        var context = getContext(),
            web = getWeb(context, siteUrl),
            spLists = web.get_lists(),
            spList = spLists.getByTitle(listName);

        context.load(spList);

        return executeQueryWithPromise(context).then(function () {
            return spList.get_id().toString();
        });
    }

    function getRootSiteUrlAsync() {
        return resolve().then(function () {
            // return cached value if available
            if (hostRootSiteUrl) {
                return hostRootSiteUrl;
            }

            var context = getContext(),
                spSite = getHostWebContext(context, hostWebUrl).get_site();

            context.load(spSite);

            return executeQueryWithPromise(context)
                .then(function () {
                    return spSite.get_url();
                })
                .finally(function (url) {
                    hostRootSiteUrl = url;
                })
                .catch(function () {
                    var err = 'Could not determine root site url';
                    console.error(err);
                    throw new Error(err);
                });
        });
    }

    function getListRootFolderUrlAsync(siteUrl, libraryName) {
        var context = getContext(),
            web = getWeb(context, siteUrl),
            list = web.get_lists().getByTitle(libraryName),
            rootFolder = list.get_rootFolder();

        context.load(rootFolder);

        return executeQueryWithPromise(context).then(
            function () {
                return {
                    url: rootFolder.get_serverRelativeUrl(),
                    success: true,
                };
            },
            function (e) {
                return {
                    error: getSpErrorMsg(e),
                    success: false,
                };
            },
        );
    }

    function checkFolderAsync(siteUrl, rootFolderUrl, folderName) {
        var context = getContext(),
            web = getWeb(context, siteUrl);

        if (folderName[0] !== '/') {
            folderName = '/' + folderName;
        }

        var folder = web.getFolderByServerRelativeUrl(
            rootFolderUrl + folderName,
        );

        context.load(folder, 'Name');

        return executeQueryWithPromise(context).then(
            function () {
                return {
                    success: true,
                };
            },
            function (e) {
                return {
                    error: getSpErrorMsg(e),
                    success: false,
                };
            },
        );
    }

    function createFolderAsync(siteUrl, libraryName, folderName) {
        var context = getContext(),
            web = getWeb(context, siteUrl),
            list = web.get_lists().getByTitle(libraryName),
            itemCreateInfo = new SP.ListItemCreationInformation();

        itemCreateInfo.set_underlyingObjectType(SP.FileSystemObjectType.folder);
        itemCreateInfo.set_leafName(folderName);

        var listItem = list.addItem(itemCreateInfo);
        listItem.update();

        context.load(listItem);

        return executeQueryWithPromise(context).then(
            function () {
                return {
                    success: true,
                };
            },
            function (e) {
                return {
                    error: getSpErrorMsg(e),
                    success: false,
                };
            },
        );
    }

    const loadTerms = (context, termSet) => {
        const terms = termSet.getAllTerms();

        context.load(terms, 'Include(IsRoot, Id, Name, PathOfTerm, Parent)');

        return terms;
    };

    const loadTermSet = (context, termSet) => {
        context.load(termSet);

        const terms = loadTerms(context, termSet);

        return {
            termSet,
            terms,
        };
    };

    const queryTermSetsFromCollection = async (context, termSets) => {
        const collectedTermSets = [];

        context.load(termSets);

        await executeQueryWithPromise(context);

        for (const termSet of termSets.get_data()) {
            const terms = loadTerms(context, termSet);

            collectedTermSets.push({ termSet, terms });
        }

        await executeQueryWithPromise(context);

        return collectedTermSets;
    };

    const getSiteContext = (siteUrl) => {
        const context = getContext();
        const siteContext = getHostWebContext(context, siteUrl).get_context();

        return siteContext;
    };

    const getTaxonomySession = (siteContext) =>
        SP.Taxonomy.TaxonomySession.getTaxonomySession(siteContext);

    async function getTermsByLabelAsync(
        siteUrl,
        searchText,
        lcid,
        matchOption,
    ) {
        const siteContext = getSiteContext(siteUrl);
        const taxSession = getTaxonomySession(siteContext);
        const termSets = taxSession
            .getDefaultSiteCollectionTermStore()
            .getTermSetsByTermLabel(searchText, lcid);
        const isStartsWith = matchOption.match(new RegExp('^startswith$', 'i'));
        const regExPattern = '^' + searchText + (!isStartsWith ? '$' : '');
        const regExpMatch = new RegExp(regExPattern, 'i');

        const queriedTermSets = await queryTermSetsFromCollection(
            siteContext,
            termSets,
        );
        const termSetObjects = termSetsToObjects(queriedTermSets, (name) =>
            name.match(regExpMatch),
        );

        return termSetObjects;
    }

    const queryTermSetsById = async (context, termStore, ids) => {
        let termSets = [];

        for (const id of ids) {
            const termSet = loadTermSet(context, termStore.getTermSet(id));

            termSets.push(termSet);
        }

        await executeQueryWithPromise(context);

        return termSets;
    };

    async function getTermSetsByIdAsync(siteUrl, termsetIds) {
        // 2023-10-05 - Removed unused lcid parameter
        const siteContext = getSiteContext(siteUrl);
        const taxSession = getTaxonomySession(siteContext);
        const termStore = taxSession.getDefaultSiteCollectionTermStore();

        const termSets = await queryTermSetsById(
            siteContext,
            termStore,
            termsetIds,
        );
        const termSetObjects = termSetsToObjects(termSets);

        return termSetObjects;
    }

    async function getTermSetsAsync(type, siteUrl, lcid, queryOptions) {
        try {
            switch (type) {
                case TERMSET_QUERY_IDS:
                    return await getTermSetsByIdAsync(
                        siteUrl,
                        queryOptions.ids,
                        lcid,
                    );
                case TERMSET_QUERY_LABEL:
                    return await getTermsByLabelAsync(
                        siteUrl,
                        queryOptions.searchText,
                        lcid,
                        queryOptions.matchOption,
                    );
                default:
                    throw new Error(`Unknown query type: ${type}`);
            }
        } catch (e) {
            throw new Error(getSpErrorMsg(e));
        }
    }

    function getListAndLibraryItem(spLists, loadFolder) {
        return spLists.map(function (spList) {
            var rootFolder = loadFolder ? spList.get_rootFolder() : null;

            return {
                baseType: spList.get_baseType(),
                id: spList.get_id().toString(),
                parentWebUrl: spList.get_parentWebUrl(),
                serverRelativeUrl: rootFolder
                    ? rootFolder.get_serverRelativeUrl()
                    : null,
                title: spList.get_title(),
            };
        });
    }

    function getListsAndLibrariesWithRootFolder(context, listsData, url) {
        listsData.forEach(function (spList) {
            var rootFolder = spList.get_rootFolder();
            context.load(rootFolder, 'ServerRelativeUrl');
        });

        return executeQueryWithPromise(context).then(
            function () {
                return {
                    items: getListAndLibraryItem(listsData, true),
                    webUrl: url,
                };
            },
            function (e) {
                throw new Error(getSpErrorMsg(e));
            },
        );
    }

    function getListsAndLibrariesBySiteUrlAsync(siteUrl, loadFolder) {
        var context = getContext(),
            web = getWeb(context, siteUrl),
            spLists = web.get_lists();

        context.load(spLists, 'Include(Id,Title,BaseType,Hidden,ParentWebUrl)');
        context.load(web);

        return executeQueryWithPromise(context).then(
            function () {
                var url = web.get_url();
                var listsData = spLists.get_data().filter(function (spList) {
                    return !spList.get_hidden();
                });

                if (!loadFolder) {
                    return {
                        items: getListAndLibraryItem(listsData, false),
                        webUrl: url,
                    };
                }

                return getListsAndLibrariesWithRootFolder(
                    context,
                    listsData,
                    url,
                );
            },
            function (e) {
                throw new Error(getSpErrorMsg(e));
            },
        );
    }

    function getBooleanValue(node, attrName) {
        var attr = node.getAttribute(attrName);
        if (!attr) {
            return false;
        }

        return attr.toLowerCase() === 'true';
    }

    function getFormattedColumn(field) {
        const schemaXml = field.get_schemaXml();
        const schema = parseXML(schemaXml);
        const schemaDom = schema.documentElement;
        const type = schemaDom.getAttribute('Type');

        return {
            hidden: getBooleanValue(schemaDom, 'Hidden'),
            id: field.get_id().toString(),
            internalName: schemaDom.getAttribute('Name'),
            readOnly: getBooleanValue(schemaDom, 'ReadOnly'),
            required: getBooleanValue(schemaDom, 'Required'),
            title: schemaDom.getAttribute('DisplayName'),
            type,
            lookupList: schemaDom.getAttribute('List'),
            richTextMode:
                type.toLowerCase() === 'note'
                    ? schemaDom.getAttribute('RichTextMode') || 'Plain'
                    : null,
            schemaXml,
        };
    }

    function getColumnsByListIdAsync(siteUrl, listId) {
        const context = getContext();
        const web = getWeb(context, siteUrl);
        const spLists = web.get_lists();
        const spList = spLists.getById(listId);
        const spFields = spList.get_fields();

        context.load(spFields, 'Include(Id, SchemaXml)');

        return executeQueryWithPromise(context).then(
            function () {
                return spFields.get_data().map(getFormattedColumn);
            },
            function (e) {
                throw new Error(getSpErrorMsg(e));
            },
        );
    }

    // 2018-06-21 - Removed unused getListByAbsoluteUrlAsync()

    function getPrincipalType(searchPeopleOnly) {
        return searchPeopleOnly
            ? SP.Utilities.PrincipalType.user
            : SP.Utilities.PrincipalType.all;
    }

    function getPrincipalSource() {
        return SP.Utilities.PrincipalSource.all;
    }

    function searchPeoplePickerUserAsync(
        siteUrl,
        searchPattern,
        searchPeopleOnly,
        maxCount = 20,
    ) {
        // 2023-10-05 removed principalType
        const context = getContext();

        var query =
            new SP.UI.ApplicationPages.ClientPeoplePickerQueryParameters();
        query.set_maximumEntitySuggestions(maxCount);
        query.set_principalType(getPrincipalType(searchPeopleOnly));
        query.set_principalSource(getPrincipalSource());
        query.set_queryString(searchPattern);
        var searchResult =
            SP.UI.ApplicationPages.ClientPeoplePickerWebServiceInterface.clientPeoplePickerSearchUser(
                context,
                query,
            );

        return executeQueryWithPromise(context).then(
            function () {
                return {
                    data: searchResult.m_value,
                    success: true,
                };
            },
            function (e) {
                return {
                    error: {
                        Message: getSpErrorMsg(e),
                    },
                    success: false,
                };
            },
        );
    }

    function deleteListItemAsync(context, list, id) {
        var item = list.getItemById(id);
        item.deleteObject();

        return executeQueryWithPromise(context);
    }

    const concatProp = (propName, value, obj) =>
        assoc(propName, obj[propName].concat(value), obj);

    const deleteItemAndUpdateResults = curry((context, list, id, results) =>
        deleteListItemAsync(context, list, id)
            .then(() => concatProp('successIds', id, results))
            .catch((e) => {
                console.error(e);
                return concatProp(
                    'failedIds',
                    { id, error: getSpErrorMsg(e) },
                    results,
                );
            }),
    );

    function deleteListItemsAsync(siteUrl, listIdentifier, useListName, ids) {
        var context = getContext();

        var web = getWeb(context, siteUrl);
        var list = getList(web, listIdentifier, useListName);

        // Deleting items one at a time in order to tell which failed and which succeeded
        // (deleting all at once gives a non-specific error if any id doesn't exist)
        return ids.reduce(
            (pResults, nextId) =>
                pResults.then(
                    deleteItemAndUpdateResults(context, list, nextId),
                ),
            resolve({ failedIds: [], successIds: [] }),
        );
    }

    // instance methods
    // wrap instance methods in the spApiFunc wrapper then return them all
    return wrapApiMethods({
        type: 'client',
        checkFolderAsync,
        createFolderAsync,
        deleteFileAsync,
        deleteListItemsAsync,
        fileExistsAsync,
        getColumnsByListIdAsync,
        getListItemAsync,
        getListIdByNameAsync,
        getListRootFolderUrlAsync,
        getListsAndLibrariesBySiteUrlAsync,
        getRootSiteUrlAsync,
        getTermSetsAsync,
        loadUserAsync,
        querySpListAsync,
        queryXmlAsync,
        searchPeoplePickerUserAsync,
        getSiteUserAsync,
        submitFileToApiPathAsync,
        submitToLibraryAsync,
        submitToListAsync,
    });
}

export default sharePointClientSideAccess;
